import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

export interface TimezoneOption {
  value: string;      // IANA name e.g. "Europe/London"
  label: string;      // display e.g. "Europe/London"
  offset: string;     // e.g. "GMT+0"
  offsetMinutes: number;
}

// Common timezones — covers major cities. Full list available via search fallback.
const COMMON_ZONES = [
  'Pacific/Auckland', 'Pacific/Fiji', 'Australia/Sydney', 'Australia/Adelaide',
  'Australia/Brisbane', 'Australia/Perth', 'Asia/Tokyo', 'Asia/Seoul',
  'Asia/Shanghai', 'Asia/Hong_Kong', 'Asia/Singapore', 'Asia/Bangkok',
  'Asia/Kolkata', 'Asia/Colombo', 'Asia/Karachi', 'Asia/Dubai',
  'Asia/Riyadh', 'Africa/Nairobi', 'Europe/Moscow', 'Europe/Istanbul',
  'Europe/Athens', 'Europe/Helsinki', 'Europe/Bucharest', 'Africa/Cairo',
  'Europe/Berlin', 'Europe/Paris', 'Europe/Rome', 'Europe/Madrid',
  'Europe/Amsterdam', 'Europe/Brussels', 'Europe/Zurich', 'Europe/Vienna',
  'Europe/Warsaw', 'Europe/Prague', 'Europe/Stockholm', 'Europe/Oslo',
  'Europe/Copenhagen', 'Europe/Dublin', 'Europe/London', 'Europe/Lisbon',
  'Atlantic/Reykjavik', 'America/Sao_Paulo', 'America/Argentina/Buenos_Aires',
  'America/Halifax', 'America/New_York', 'America/Toronto', 'America/Chicago',
  'America/Denver', 'America/Phoenix', 'America/Los_Angeles', 'America/Vancouver',
  'America/Anchorage', 'Pacific/Honolulu', 'America/Mexico_City', 'America/Bogota',
  'America/Lima', 'America/Santiago', 'Africa/Lagos', 'Africa/Johannesburg',
  'Africa/Casablanca', 'UTC',
];

let cachedOptions: TimezoneOption[] | null = null;

const getOffsetString = (tz: string): { offset: string; offsetMinutes: number } => {
  const now = new Date();
  const raw = formatInTimeZone(now, tz, 'xxx'); // e.g. "+05:30" or "-05:00"
  const sign = raw[0] === '-' ? -1 : 1;
  const [h, m] = raw.slice(1).split(':').map(Number);
  const totalMinutes = sign * (h * 60 + m);
  const absH = Math.abs(Math.floor(totalMinutes / 60));
  const absM = Math.abs(totalMinutes % 60);
  const offset = absM > 0
    ? `GMT${totalMinutes >= 0 ? '+' : '-'}${absH}:${String(absM).padStart(2, '0')}`
    : `GMT${totalMinutes >= 0 ? '+' : '-'}${absH}`;
  return { offset: totalMinutes === 0 ? 'GMT+0' : offset, offsetMinutes: totalMinutes };
};

let cachedAllOptions: TimezoneOption[] | null = null;

const buildOptions = (zones: string[]): TimezoneOption[] =>
  zones.map((tz) => {
    const { offset, offsetMinutes } = getOffsetString(tz);
    return { value: tz, label: tz, offset, offsetMinutes };
  }).sort((a, b) => a.offsetMinutes - b.offsetMinutes);

/** Returns common timezones (default view) */
export const getTimezoneOptions = (): TimezoneOption[] => {
  if (cachedOptions) return cachedOptions;
  // Ensure detected tz is always in the list
  const detected = getDetectedTimezone();
  const zones = COMMON_ZONES.includes(detected)
    ? COMMON_ZONES
    : [detected, ...COMMON_ZONES];
  cachedOptions = buildOptions(zones);
  return cachedOptions;
};

/** Returns ALL IANA timezones (for search) */
const getAllTimezoneOptions = (): TimezoneOption[] => {
  if (cachedAllOptions) return cachedAllOptions;
  try {
    const zones = (Intl as any).supportedValuesOf('timeZone') as string[];
    cachedAllOptions = buildOptions(zones);
  } catch {
    cachedAllOptions = getTimezoneOptions();
  }
  return cachedAllOptions;
};

export const getDetectedTimezone = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone;

export const searchTimezones = (
  query: string,
  detected?: string,
): TimezoneOption[] => {
  if (!query.trim()) {
    // No query — show common list with detected first
    const options = getTimezoneOptions();
    if (!detected) return options;
    const detectedOption = options.find((o) => o.value === detected);
    if (!detectedOption) return options;
    return [detectedOption, ...options.filter((o) => o.value !== detected)];
  }

  // When searching, search ALL timezones
  const allOptions = getAllTimezoneOptions();
  const q = query.toLowerCase().replace(/[/_]/g, ' ');
  return allOptions.filter((o) => {
    const searchable = `${o.label.replace(/[/_]/g, ' ')} ${o.offset}`.toLowerCase();
    return searchable.includes(q);
  });
};

/** Get a short display name: "Europe/London" → "London" */
export const shortTzName = (tz: string): string => {
  const parts = tz.split('/');
  return parts[parts.length - 1].replace(/_/g, ' ');
};

/** Friendly timezone display: "Europe/London" → "London, Europe" */
export const friendlyTzName = (tz: string): string => {
  const parts = tz.split('/');
  if (parts.length < 2) return tz;
  const city = parts[parts.length - 1].replace(/_/g, ' ');
  const region = parts[0].replace(/_/g, ' ');
  return `${city}, ${region}`;
};

/**
 * Returns the current wall-clock hours and minutes in a given timezone.
 * Useful for positioning the "now" indicator on the timeline.
 */
export const getNowInTz = (tz: string): { hours: number; minutes: number; dayOfWeek: number } => {
  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour: 'numeric', minute: 'numeric', weekday: 'short',
    hour12: false,
  }).formatToParts(now);

  let hours = 0, minutes = 0, dayOfWeek = 0;
  const dayMap: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  for (const p of parts) {
    if (p.type === 'hour') hours = parseInt(p.value, 10);
    if (p.type === 'minute') minutes = parseInt(p.value, 10);
    if (p.type === 'weekday') dayOfWeek = dayMap[p.value] ?? 0;
  }
  // Intl hour12:false returns 24 for midnight in some locales
  if (hours === 24) hours = 0;
  return { hours, minutes, dayOfWeek };
};

/**
 * Interprets a date ("YYYY-MM-DD") and time ("HH:mm") as wall-clock time
 * in `tz` and returns the correct UTC ISO-8601 string.
 */
export const wallClockToISO = (date: string, time: string, tz: string): string => {
  const naive = new Date(`${date}T${time}:00`);
  return fromZonedTime(naive, tz).toISOString();
};

/**
 * Like wallClockToISO but handles end times that cross midnight.
 * If endTime < startTime (as strings), the end is interpreted as the next day.
 */
export const wallClockEndToISO = (date: string, startTime: string, endTime: string, tz: string): string => {
  if (endTime >= startTime) return wallClockToISO(date, endTime, tz);
  // Cross-midnight: add one day to the date for the end time
  const [y, m, d] = date.split('-').map(Number);
  const nextDay = new Date(Date.UTC(y, m - 1, d + 1));
  const nextDateStr = `${nextDay.getUTCFullYear()}-${String(nextDay.getUTCMonth() + 1).padStart(2, '0')}-${String(nextDay.getUTCDate()).padStart(2, '0')}`;
  return wallClockToISO(nextDateStr, endTime, tz);
};

/**
 * Formats a start–end range in a target timezone, using the start time's
 * UTC offset for both times. This avoids DST transitions mid-range showing
 * inconsistent timezone abbreviations or offset-shifted end times.
 */
export const formatRangeInTz = (
  startUtc: Date,
  endUtc: Date,
  tz: string,
  opts?: { includeDate?: boolean },
): string => {
  // Get the offset at the start time
  const startOffsetStr = formatInTimeZone(startUtc, tz, 'xxx'); // e.g. "+01:00"
  const tzAbbr = formatInTimeZone(startUtc, tz, 'zzz'); // e.g. "CET"

  // Apply the start's offset to both times for consistent display
  const parseOffset = (str: string): number => {
    const sign = str[0] === '-' ? -1 : 1;
    const [h, m] = str.slice(1).split(':').map(Number);
    return sign * (h * 60 + m);
  };
  const offsetMs = parseOffset(startOffsetStr) * 60 * 1000;

  const startLocal = new Date(startUtc.getTime() + offsetMs);
  const endLocal = new Date(endUtc.getTime() + offsetMs);

  const pad = (n: number) => String(n).padStart(2, '0');
  const fmtTime = (d: Date) => {
    const h = d.getUTCHours();
    const m = d.getUTCMinutes();
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${pad(m)} ${ampm}`;
  };

  const startTime = fmtTime(startLocal);
  const endTime = fmtTime(endLocal);

  if (opts?.includeDate) {
    const dateStr = formatInTimeZone(startUtc, tz, 'EEE d MMM');
    return `${dateStr}, ${startTime} – ${endTime} ${tzAbbr}`;
  }

  return `${startTime} – ${endTime} ${tzAbbr}`;
};

/**
 * Formats a date+time (understood as wall-clock in `fromTz`) into the
 * viewer's timezone (`toTz`).  Returns null when both timezones match.
 */
export const formatInOtherTz = (
  date: string,
  time: string,
  fromTz: string,
  toTz: string,
): string | null => {
  if (fromTz === toTz) return null;
  const utcDate = fromZonedTime(new Date(`${date}T${time}:00`), fromTz);
  return formatInTimeZone(utcDate, toTz, 'EEE d MMM, h:mm a zzz');
};
